import React from 'react';
import { ProjectTodo } from '../ducks';
import { RowState } from '../selectors';
import { useScheduleCompletion } from '@/features/todos/useScheduleCompletion';

interface ProjectStatusCellProps {
  todo: ProjectTodo;
  rowState: RowState;
  hasChildren: boolean;
  // Edit mode: the parent glyph becomes a clickable status toggle and the leaf
  // checkbox completes immediately (onToggleStatus, projects-slice optimistic).
  // View mode: the parent glyph is read-only and the leaf checkbox runs the
  // schedule-complete (5s-undo) flow via onUpdate.
  editable: boolean;
  onUpdate?: (update: { id: string; [key: string]: unknown }) => void;
  onToggleStatus?: (e: React.SyntheticEvent) => void;
}

// Status affordance shared by the editable Outline (TodoRow) and the read-only
// Inbox (InboxProjectRow) so the SAME task renders the SAME way in both: a
// completion checkbox for leaves, a status glyph for parents. Only interactivity
// differs by mode — the markup is identical, which is the whole point of the
// unification (no more "▶ button here, checkbox there" for the same row).
export function ProjectStatusCell({
  todo,
  rowState,
  hasChildren,
  editable,
  onUpdate,
  onToggleStatus,
}: ProjectStatusCellProps) {
  // Hook runs unconditionally (rules of hooks); inert in edit mode (onUpdate
  // gate undefined) where leaf completion goes through onToggleStatus instead.
  const completion = useScheduleCompletion(todo, editable ? undefined : onUpdate);

  const isDone = rowState === 'done';
  const isBlocked = rowState === 'blocked';
  const glyph = isDone ? '✓' : isBlocked ? '◌' : '▶';

  if (hasChildren) {
    const title = isDone
      ? editable
        ? 'Mark as not done'
        : 'Done'
      : isBlocked
        ? editable
          ? 'Complete subtasks before closing this task'
          : 'Blocked by open subtasks'
        : editable
          ? 'Mark as done'
          : 'Ready';
    if (editable) {
      return (
        <button
          type="button"
          className={`todo-row__status todo-row__status--${rowState}`}
          onClick={onToggleStatus}
          aria-label={title}
          title={title}
          disabled={isBlocked && !isDone}
        >
          {glyph}
        </button>
      );
    }
    return (
      <span
        className={`todo-row__status todo-row__status--${rowState}`}
        aria-label={title}
        title={title}
        aria-hidden="true"
      >
        {glyph}
      </span>
    );
  }

  // Leaf — completion checkbox, identical markup in both modes.
  const checked = editable ? isDone : completion.isChecked;
  return (
    <input
      type="checkbox"
      className="todo-row__checkbox"
      checked={checked}
      onChange={() => {}}
      onClick={editable ? onToggleStatus : completion.toggle}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (editable) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleStatus?.(e);
          }
        } else {
          completion.onKeyDown(e);
        }
      }}
      aria-label={todo.title}
      aria-checked={checked}
    />
  );
}
